import { pick } from 'es-toolkit'
import { RxDocument, RxDocumentData, shuffleArray } from 'rxdb'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { Card } from '~/db/types'

import { canPlayCard } from '../boundedContext/cards/utils/card'

import { useFadingCardStore } from './fading-card'

type CardSnoozeProps = Pick<Card, 'snoozedUntilSource' | 'snoozeCountSource' | 'snoozedUntilLearn' | 'snoozeCountLearn'>

type CardsGameActions = {
  decreaseActiveItemIndex: () => void
  getSerializedState: () => CardsGameState
  increaseActiveItemIndex: () => void
  onRemove: (id: string) => void
  onUpdate: (updatedCard: RxDocumentData<Card>) => void
  rememberSnoozePropsBeforePlay: (cardId: string, snoozeProps: CardSnoozeProps) => void
  // TODO: make type Card type as generic
  reset: (cardsAfterReset?: RxDocument<Card>[]) => void
  resolveDocumentsFromIds: (collectionDocs: RxDocument<Card>[]) => void
  setAllCards: (allCards: RxDocument<Card>[]) => void
  setIsInitialized: (isInitialized: boolean) => void
  setNotGuessedCards: (value: RxDocument<Card>[]) => void
  takeSnoozePropsBeforePlay: (cardId: string) => CardSnoozeProps | undefined
}

export type CardsGameState = {
  activeItemIndex: number
  allCards: RxDocument<Card>[]
  isInitialized: boolean
  notGuessedCardIds: string[]
  notGuessedCards: RxDocument<Card>[]
  shuffledCardIds: string[]
  shuffledCards: RxDocument<Card>[]
  snoozePropsBeforePlayByCardId: Record<string, CardSnoozeProps>
}

export const useCardsGameStore = create<CardsGameState & CardsGameActions>()(
  persist(
    (set, get) => ({
      activeItemIndex: 0,
      allCards: [],
      notGuessedCardIds: [],
      notGuessedCards: [],
      shuffledCardIds: [],
      shuffledCards: [],
      snoozePropsBeforePlayByCardId: {},
      isInitialized: false,

      rememberSnoozePropsBeforePlay: (cardId, snoozeProps) => {
        set(state => ({
          snoozePropsBeforePlayByCardId: { ...state.snoozePropsBeforePlayByCardId, [cardId]: snoozeProps },
        }))
      },

      takeSnoozePropsBeforePlay: cardId => {
        const snoozeProps = get().snoozePropsBeforePlayByCardId[cardId]
        if (!snoozeProps) return

        set(state => {
          const { [cardId]: removedSnoozeProps, ...remainingSnoozeProps } = state.snoozePropsBeforePlayByCardId
          void removedSnoozeProps

          return { snoozePropsBeforePlayByCardId: remainingSnoozeProps }
        })

        return snoozeProps
      },

      onRemove: removedCardId => {
        set(state => {
          const prevShuffled = state.shuffledCards
          const nextShuffled = prevShuffled.filter(shuffledItem => shuffledItem.id !== removedCardId)
          const removedCardIndex = prevShuffled.findIndex(item => item.id === removedCardId)
          if (removedCardIndex === -1)
            return { shuffledCards: nextShuffled, shuffledCardIds: nextShuffled.map(({ id }) => id) }

          const prevActive = state.activeItemIndex
          const noCardsLeft = !prevShuffled[prevActive]
          const isCardEqualOrAfterActive = prevActive >= removedCardIndex
          const nextActive = noCardsLeft || !isCardEqualOrAfterActive ? prevActive : prevActive - 1

          return {
            activeItemIndex: nextActive,
            shuffledCardIds: nextShuffled.map(({ id }) => id),
            shuffledCards: nextShuffled,
          }
        })

        set(state => ({
          notGuessedCardIds: state.notGuessedCardIds.filter(notGuessedCardId => notGuessedCardId !== removedCardId),
          notGuessedCards: state.notGuessedCards.filter(notGuessedCard => notGuessedCard.id !== removedCardId),
        }))
      },

      onUpdate: updatedCard => {
        // This is necessary for a proper cards-redirect logic
        set(({ allCards }) => ({
          allCards: allCards.map(card => (card.id === updatedCard.id ? card.getLatest() : card)),
        }))

        set(state => {
          const prevShuffled = state.shuffledCards
          const nextShuffled = [...prevShuffled]
          const updatedCardRxDocumentIdx = prevShuffled.findIndex(item => item.id === updatedCard.id)
          if (updatedCardRxDocumentIdx === -1) return state

          const latestUpdatedCard = nextShuffled[updatedCardRxDocumentIdx]?.getLatest()
          if (!latestUpdatedCard) return state

          nextShuffled[updatedCardRxDocumentIdx] = latestUpdatedCard

          return { shuffledCards: nextShuffled }
        })
      },

      reset: cardsAfterReset => {
        useFadingCardStore.getState().clearFadingCard()

        set(state => {
          const cardsForGame = (cardsAfterReset ?? state.allCards ?? []).filter(card => canPlayCard(card))
          const newShuffledCards = shuffleArray(cardsForGame)

          return {
            activeItemIndex: newShuffledCards.length - 1,
            notGuessedCardIds: [],
            notGuessedCards: [],
            shuffledCardIds: newShuffledCards.map(({ id }) => id),
            shuffledCards: newShuffledCards,
            snoozePropsBeforePlayByCardId: {},
          }
        })
      },

      resolveDocumentsFromIds: collectionDocs => {
        if (!collectionDocs.length) return

        const shouldReset = !get().shuffledCardIds.length
        if (shouldReset) return get().reset(collectionDocs)

        const idToCardMap = new Map(collectionDocs.map(card => [card.id, card]))
        set(state => {
          const shuffled = state.shuffledCardIds
            .map(id => idToCardMap.get(id))
            .filter(card => !!card) as RxDocument<Card>[]
          const notGuessed = state.notGuessedCardIds
            .map(id => idToCardMap.get(id))
            .filter(card => !!card) as RxDocument<Card>[]

          return {
            notGuessedCardIds: notGuessed.map(({ id }) => id),
            notGuessedCards: notGuessed,
            shuffledCardIds: shuffled.map(({ id }) => id),
            shuffledCards: shuffled,
          }
        })

        set(state => {
          const { activeItemIndex, shuffledCards } = state

          if (!shuffledCards.length) return { activeItemIndex: -1 }

          if (activeItemIndex >= shuffledCards.length) return { activeItemIndex: shuffledCards.length - 1 }

          return { activeItemIndex }
        })
      },

      decreaseActiveItemIndex: () => {
        set(state => ({ activeItemIndex: state.activeItemIndex - 1 }))
      },
      increaseActiveItemIndex: () => {
        set(state => ({ activeItemIndex: state.activeItemIndex + 1 }))
      },

      setAllCards: allCards => set({ allCards }),

      setNotGuessedCards: value => {
        set(() => ({
          notGuessedCardIds: value.map(({ id }) => id),
          notGuessedCards: value,
        }))
      },

      setIsInitialized: isInitialized => set({ isInitialized }),

      getSerializedState: () =>
        pick(get(), [
          'activeItemIndex',
          'allCards',
          'isInitialized',
          'notGuessedCardIds',
          'notGuessedCards',
          'shuffledCardIds',
          'shuffledCards',
          'snoozePropsBeforePlayByCardId',
        ]),
    }),
    {
      name: 'lingo-cards-game',
      partialize: state => ({
        activeItemIndex: state.activeItemIndex,
        notGuessedCardIds: state.notGuessedCardIds,
        shuffledCardIds: state.shuffledCardIds,
        snoozePropsBeforePlayByCardId: state.snoozePropsBeforePlayByCardId,
      }),
    },
  ),
)
